import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Typography, FormControl, InputLabel,
} from '@mui/material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import ImageUploadModal from '../../../utils/ImageUploadModal';
import { useAuth } from '../../../context/AuthContext';
import OrderByOptionService from '../../setting/orderByOption/OrderByOptionService';
import { reasonOf, CARD_STATUS, MODE } from './dispatchReasons';

// One dispatch-bot task. Each kind of task shows only the actions that make sense for it; every
// action that ends with a purchase needs the proof image (pasted, like the purchase-request
// modal) and names the buyer. "ซื้อไม่ทัน" = the order enters the cancel page, like an admin
// cancel — refund and customer notice happen there. Thai: admins use it.

const t = (iso) => (iso ? format(new Date(iso), 'd MMM HH:mm') : '—');

/** Which buttons a task gets. */
const kindOf = (job) => {
  const r = job.adminReason;
  if (r === 'proof_to_customer') return 'inform'; // done with a proof: copy it to the customer's chat
  if (['direct_buyer', 'fallback_buyer'].includes(r)) return 'fuji';
  if (r === 'sold_ask_buyer') return 'askBuyer';
  if (['sold_before_send', 'sold_nobody_opened'].includes(r)) return 'fyi';
  if (r === 'double_proof') return 'double';
  if (r === 'buyer_silent') return 'silent'; // Shops: the engaged buyer went quiet
  if (r && r.startsWith('pinned_')) return 'pinned';
  return 'queue'; // no_candidate, all_passed, outside_hours, last_no_response, sold_out_by_clicker
};

/** An instruction: the first part on its own line, each " · " alternative on its own line below. */
const Steps = ({ text }) => {
  const [first, ...rest] = text.split(' · ');
  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>{first}</Typography>
      {rest.map((line) => <Typography key={line} variant="body2" sx={{ pl: 2 }}>{line}</Typography>)}
    </Box>
  );
};

/** The loud late task in parts: what happened, step 1 (check the item), then the two ways it ends side by side. */
const TONE = {
  go: { border: '#2e7d32', bg: '#e8f5e9', text: '#1b5e20', icon: '✅' },
  stop: { border: '#ef6c00', bg: '#fff3e0', text: '#e65100', icon: '✋' },
};
const LoudSteps = ({ steps, url }) => (
  <Box sx={{ display: 'grid', gap: 1.5, mt: 0.5 }}>
    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem' }}>{steps.head}</Typography>
    <Box sx={{ bgcolor: '#fff', color: 'text.primary', borderRadius: 1, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Chip label="1" size="small" sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 800 }} />
      <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 200 }}>{steps.first}</Typography>
      {url && <Button size="small" variant="contained" color="error" href={url} target="_blank" rel="noopener">เปิดลิงก์สินค้า ↗</Button>}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Chip label="2" size="small" sx={{ bgcolor: '#fff', color: '#d32f2f', fontWeight: 800 }} />
      <Typography sx={{ color: '#fff', fontWeight: 700 }}>ทำตามผลที่เช็ค</Typography>
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
      {steps.branches.map((b) => {
        const tone = TONE[b.tone] || TONE.go;
        return (
          <Box key={b.title} sx={{ bgcolor: '#fff', borderRadius: 1, borderTop: `6px solid ${tone.border}`, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: tone.bg, px: 1.5, py: 1 }}>
              <Typography sx={{ color: tone.text, fontWeight: 800 }}>ถ้า{b.title}</Typography>
              <Typography sx={{ color: tone.text, fontWeight: 800, fontSize: '1.1rem' }}>{tone.icon} {b.action}</Typography>
            </Box>
            <Box component="ol" sx={{ m: 0, py: 1, pl: 4, pr: 1.5, color: 'text.primary' }}>
              {b.lines.map((line) => <Typography component="li" key={line} variant="body2" sx={{ py: 0.25 }}>{line}</Typography>)}
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

/** A proof picture: the winner's lives on the order, a losing (double) one on its card. */
const Proof = ({ url, size = 56 }) => {
  if (!url) return '—';
  if (url === 'pending_upload') return <span style={{ color: '#999' }}>กำลังอัปโหลด…</span>;
  return (
    <Link href={url} target="_blank" rel="noopener">
      <img src={url} alt="หลักฐาน" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
    </Link>
  );
};

// Tasks that may wait for the morning instead of "tell North": nobody took it and there is no fallback.
const CAN_DEFER = ['no_candidate', 'all_passed', 'outside_hours', 'last_no_response', 'pinned_no_response'];
// Reasons where Mercari (or the buyer) reported the item sold: only these may end in ซื้อไม่ทัน or a
// refund. Everything else must end with a purchase (owner 2026-11-02: never drop a job because
// nobody bought — another buyer, the morning round, or buy it yourself).
const SOLD_REASONS = ['sold_before_send', 'sold_nobody_opened', 'sold_waiting_proof', 'sold_ask_buyer', 'sold_out_by_clicker'];

export default function DispatchTaskModal({ job, onClose, onAcknowledge, onCancelOrder, onProof, onPass, onInformed, onForceSend, onDefer, onRefundPurchase }) {
  const [busy, setBusy] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [buyerId, setBuyerId] = useState('');
  const [forceBuyerId, setForceBuyerId] = useState(''); // "ส่งการ์ดให้คนนี้เลย" — any buyer with a channel
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';

  const kind = job ? kindOf(job) : null;
  // Sea job, pinned buyer still silent after 30 min (owner 2026-11-03): FujiKing may buy it
  // instead (Exclusive items stay with the buyer), so the proof asks who bought instead of assuming.
  const pinnedLate = kind === 'pinned' && reasonOf(job).late;
  const cards = (job?.offers || []).filter((o) => o.status !== 'assigned' && o.kind !== 'admin'); // real cards only
  const fixedBuyer = kind === 'fuji' || kind === 'silent' ? job.currentOffer?.orderer
    : kind === 'pinned' && !pinnedLate ? (job.offers || []).find((o) => o.kind === 'pinned')?.orderer : null;
  const needsPicker = kind === 'askBuyer' || kind === 'queue' || kind === 'fyi' || pinnedLate; // fyi = sold task: "still buyable → who bought it"

  useEffect(() => {
    setProofFile(null);
    setPasteOpen(false);
    if (!job) return;
    // Preset: the buyer who opened the item link (ask-buyer), else nothing chosen.
    const opener = (job.offers || []).find((o) => o.openedAt);
    setBuyerId(kind === 'askBuyer' && opener ? String(opener.orderByOptionId) : '');
    setForceBuyerId('');
    const live = !['done', 'admin_taken', 'cancelled'].includes(job.status) && !(job.status === 'sold_out' && job.adminAcknowledgedAt); // the force-send picker needs the list too
    if ((needsPicker || live) && buyers.length === 0) {
      OrderByOptionService.getActiveOrderByOptions().then((res) => setBuyers(res.data || [])).catch(() => {});
    }
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  // Late pinned task (sea only): preset "who bought" to FujiKing — the usual way it ends.
  useEffect(() => {
    if (!pinnedLate || buyerId || buyers.length === 0) return;
    const pick = buyers.find((b) => /fuji/i.test(b.label || ''));
    if (pick) setBuyerId(String(pick.id));
  }, [pinnedLate, buyers, job]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!job) return null;
  // A site purchase request (plan E): no order until the proof — the job's `subject` carries the
  // item and the customer in the same shape, so everything below reads one `order` object.
  const subject = job.subject || null;
  const isPurchase = !job.order && subject?.kind === 'purchase';
  const order = job.order || (subject ? {
    id: null, url: subject.url, imageUrl: subject.imageUrl, amount: subject.priceJpy, qty: 1,
    smileNameInput: subject.customer, contact: subject.contact, shop: subject.shop, purchaseConfirmUrl: subject.proofUrl,
  } : {});
  const baseReason = reasonOf(job);
  // Sold on a purchase request: the money comes back through the button below, not the cancel tab.
  const reason = isPurchase && ['sold_before_send', 'sold_nobody_opened'].includes(job.adminReason)
    ? { ...baseReason, todo: 'กรุณายืนยันว่าซื้อไม่ทันจริง ก่อนดำเนินการต่อ · ตรวจในเว็บก่อน · ซื้อไม่ทันจริง → กด "คืนเงินลูกค้า" คืนเข้า wallet เต็มจำนวนและแจ้งลูกค้าทาง LINE' }
    : baseReason;
  const price = (Number(order.amount) || 0) * Math.max(1, Number(order.qty) || 1);
  const final = ['done', 'sold_out', 'admin_taken', 'cancelled'].includes(job.status);
  // A sold task nobody confirmed yet is still open (owner 2026-11-01): either it really sold
  // (confirm → cancel / refund) or someone can still buy it (proof, or a fresh card).
  const openSold = job.status === 'sold_out' && !job.adminAcknowledgedAt;
  const actionable = !final || openSold;
  // The refund stays possible on the sold task (status sold_out) until it is acknowledged.
  const reportedSold = SOLD_REASONS.includes(job.adminReason);
  const canRefund = isPurchase && reportedSold && Boolean(onRefundPurchase) && !job.adminAcknowledgedAt && !['done', 'admin_taken', 'cancelled'].includes(job.status);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(job); } finally { setBusy(false); }
  };
  const chosenBuyerId = fixedBuyer ? fixedBuyer.id : (buyerId ? Number(buyerId) : null);
  const canConfirm = Boolean(proofFile) && Boolean(chosenBuyerId) && !busy;
  // "แจ้งลูกค้า": the proof shown big, straight away — the admin copies it into the chat.
  const proofImg = order.purchaseConfirmUrl && order.purchaseConfirmUrl !== 'pending_upload' ? order.purchaseConfirmUrl : null;
  const copyProof = async () => {
    try {
      const blob = await (await fetch(proofImg)).blob();
      let png = blob;
      if (blob.type !== 'image/png') { // the clipboard accepts PNG only
        const bmp = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = bmp.width; canvas.height = bmp.height;
        canvas.getContext('2d').drawImage(bmp, 0, 0);
        png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      }
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': png })]);
      toast.success('คัดลอกรูปแล้ว — ไปวางในแชทลูกค้าได้เลย');
    } catch {
      toast.error('คัดลอกอัตโนมัติไม่ได้ — คลิกขวาที่รูปแล้วเลือก Copy Image');
    }
  };

  const proofBlock = (label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {needsPicker ? (
        <Select size="small" displayEmpty value={buyerId} onChange={(e) => setBuyerId(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="" disabled>ใครเป็นคนซื้อ…</MenuItem>
          {buyers.map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.label}</MenuItem>)}
        </Select>
      ) : null}
      <Button variant="outlined" size="small" onClick={() => setPasteOpen(true)}>
        📷 {proofFile ? 'เปลี่ยนรูป' : 'วางรูปยืนยันการสั่งซื้อ (Ctrl+V)'}
      </Button>
      {proofFile && <img src={URL.createObjectURL(proofFile)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />}
      <Button variant="contained" disabled={!canConfirm} onClick={() => run((j) => onProof(j, proofFile, chosenBuyerId))}>
        {label}
      </Button>
    </Box>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Chip size="small" color={reason.color} label={reason.label} />
          {job.shopItem && <Chip size="small" variant="outlined" color="warning" label="Shops · อาจมีหลายชิ้น" />}
          <Typography variant="subtitle1">งานกด #{job.id} · {MODE[job.mode] || job.mode}{job.isBig ? ' · ชิ้นใหญ่' : ''}{job.noTracking ? ' · ไม่มีเลขพัสดุ' : ''}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        {/* No "ask whoever opened it first": a standard item is 1 piece, so a second order just fails;
            a Shops item never moves on while someone has the link open (buyer_silent handles that). */}
        {reason.todo && (reason.loud ? (
          // Loud: filled red, big title, slow pulse — admins missed the plain box (owner 2026-11-03).
          <Alert
            severity="error"
            variant="filled"
            sx={{
              border: '3px solid #b71c1c',
              animation: 'dispatchLoudPulse 1.6s ease-in-out infinite',
              '@keyframes dispatchLoudPulse': { '0%, 100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.6)' }, '50%': { boxShadow: '0 0 0 8px rgba(211,47,47,0)' } },
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <AlertTitle sx={{ fontSize: '1.3rem', fontWeight: 800 }}>🚨 {reason.label}</AlertTitle>
            {reason.steps ? <LoudSteps steps={reason.steps} url={order.url} /> : <Steps text={reason.todo} />}
          </Alert>
        ) : (
          <Alert severity={reason.color === 'error' ? 'error' : 'info'}><Steps text={reason.todo} /></Alert>
        ))}

        <Box display="flex" gap={2} alignItems="flex-start">
          {order.imageUrl && <img src={order.imageUrl} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6 }} />}
          <Box>
            <Typography variant="body2">{isPurchase ? `คำขอซื้อจากเว็บ #${subject.id} (ยังไม่มีออเดอร์)` : `ออเดอร์ #${order.id}`} · {order.smileNameInput || ''} · {order.shop || ''}</Typography>
            <Typography variant="body2">¥{price.toLocaleString()}{Number(order.qty) > 1 ? ` (${order.qty} ชิ้น)` : ''}</Typography>
            {order.url && <Link href={order.url} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all', fontSize: '0.8rem' }}>{order.url}</Link>}
            <Typography variant="caption" display="block" color="text.secondary">
              สร้างงาน {t(job.createdAt)} · ต้องการแอดมินตั้งแต่ {t(job.needsAdminAt)}
            </Typography>
          </Box>
        </Box>

        {/* แจ้งลูกค้า: who to message (one click copies the contact) and the proof image full
            size right here — copy, paste in the chat, close. The per-buyer card evidence
            means nothing for this task, so it is not shown. */}
        {kind === 'inform' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body1" fontWeight={700}>ลูกค้า: {order.contact || order.smileNameInput || '—'}</Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={!(order.contact || order.smileNameInput)}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(order.contact || order.smileNameInput);
                  toast.success('คัดลอกชื่อลูกค้าแล้ว');
                } catch {
                  toast.error('คัดลอกไม่สำเร็จ');
                }
              }}
            >
              📋 คัดลอกชื่อ
            </Button>
          </Box>
        )}
        {kind === 'inform' && (
          proofImg ? (
            <img src={proofImg} alt="รูปยืนยันการซื้อ" style={{ maxWidth: '100%', maxHeight: 440, objectFit: 'contain', borderRadius: 8, justifySelf: 'start' }} />
          ) : (
            <Alert severity="warning">รูปกำลังอัปโหลด… ปิดหน้าต่างนี้แล้วเปิดใหม่อีกครั้ง</Alert>
          )
        )}

        {/* Double proof (owner 2026-11-02): the two pictures side by side — the one that counts (on
            the order) and every duplicate, admin-attached ones too (the cards table hides those). */}
        {kind === 'double' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>หลักฐานซ้ำ — เทียบ 2 ชุด</Typography>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" display="block" color="text.secondary">บนออเดอร์ (ชุดที่นับ)</Typography>
                <Proof url={order.purchaseConfirmUrl} size={180} />
              </Box>
              {(job.offers || []).filter((o) => o.status === 'double_proof').map((o) => (
                <Box key={o.id}>
                  <Typography variant="caption" display="block" color="text.secondary">
                    ซ้ำ — {o.orderer?.label || o.orderByOptionId}{o.kind === 'admin' ? ' (แอดมินวางให้)' : ''} · {t(o.respondedAt)}
                  </Typography>
                  <Proof url={o.proofUrl} size={180} />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {cards.length > 0 && kind !== 'fuji' && kind !== 'inform' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>การ์ดที่ส่ง — ใครเปิดอะไรตอนไหน</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>กลุ่มกด</TableCell>
                  <TableCell>ส่งการ์ด</TableCell>
                  <TableCell>เปิดลิงก์สินค้า</TableCell>
                  <TableCell>เปิดหน้าหลักฐาน</TableCell>
                  <TableCell>กดไม่ได้</TableCell>
                  <TableCell>สถานะการ์ด</TableCell>
                  <TableCell>รูปหลักฐาน</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cards.map((o) => {
                  const late = o.openedAt && o.respondedAt && new Date(o.openedAt) > new Date(o.respondedAt) && o.status === 'double_proof';
                  return (
                    <TableRow key={o.id} sx={o.status === 'done' ? { bgcolor: 'success.light' } : undefined}>
                      <TableCell>{o.orderer?.label || o.orderByOptionId}{o.kind === 'pinned' ? ' (เจาะจง)' : ''}</TableCell>
                      <TableCell>{o.status === 'assigned' ? '—' : t(o.sentAt)}</TableCell>
                      <TableCell>{o.openedAt ? `${t(o.openedAt)}${late ? ' (เปิดหลังปิดงาน)' : ''}` : <span style={{ color: '#999' }}>ไม่เคยเปิด</span>}</TableCell>
                      <TableCell>{o.proofOpenedAt ? t(o.proofOpenedAt) : '—'}</TableCell>
                      <TableCell>{o.declinedAt ? t(o.declinedAt) : '—'}</TableCell>
                      <TableCell>{CARD_STATUS[o.status] || o.status}</TableCell>
                      <TableCell><Proof url={o.status === 'done' ? order.purchaseConfirmUrl : o.proofUrl} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {!final && kind === 'fuji' && proofBlock('สั่งผ่าน FujiKing แล้ว ✓')}
        {!final && kind === 'askBuyer' && proofBlock('คนกดบอกว่ากดแล้ว ✓')}
        {!final && kind === 'queue' && proofBlock('ซื้อแล้ว (นอกบอท) ✓')}
        {openSold && kind === 'fyi' && proofBlock('ยังซื้อได้ — ซื้อแล้ว ✓')}
        {!final && kind === 'pinned' && proofBlock(pinnedLate ? 'ซื้อแล้ว ✓' : `${fixedBuyer?.label || 'คนกด'} ซื้อแล้ว ✓`)}
        {!final && kind === 'silent' && proofBlock(`${fixedBuyer?.label || 'คนกด'} กดแล้ว ✓`)}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {!final && kind === 'silent' && (
          <Button variant="outlined" disabled={busy} onClick={() => window.confirm(`${fixedBuyer?.label || 'คนกด'} ไม่ได้กด? การ์ดของเขาจะปิด แล้วส่งให้คนถัดไป`) && run(onPass)}>
            ไม่ได้กด → ส่งต่อคนถัดไป
          </Button>
        )}
        {!final && !isPurchase && reportedSold && ['fuji', 'askBuyer', 'queue', 'silent'].includes(kind) && (
          <Button variant="outlined" color="error" disabled={busy} onClick={() => window.confirm('ซื้อไม่ทัน? รายการจะเข้าหน้า ดำเนินการยกเลิก (คืนเงินและแจ้งลูกค้าที่นั่น)') && run(onCancelOrder)}>
            {kind === 'askBuyer' ? 'คนกดบอกว่าไม่ได้กด → ซื้อไม่ทัน' : 'ซื้อไม่ทัน'}
          </Button>
        )}
        {kind === 'inform' && (
          <Button variant="outlined" disabled={!proofImg || busy} onClick={copyProof}>📋 คัดลอกรูป</Button>
        )}
        {!final && onDefer && CAN_DEFER.includes(job.adminReason) && (
          <Button variant="outlined" disabled={busy} onClick={() => window.confirm('เลื่อนไปตอนเช้า? งานนี้จะหายจากบอร์ด แล้วบอทส่งการ์ดใหม่ให้ทุกคนตอน 07:00 (การ์ดเดิมยังกดได้จนถึงตอนนั้น)') && run(onDefer)}>
            เลื่อนไปส่งตอนเช้า 07:00
          </Button>
        )}
        {actionable && kind !== 'inform' && onForceSend && (
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>ส่งการ์ดให้คนอื่น</InputLabel>
              <Select value={forceBuyerId} label="ส่งการ์ดให้คนอื่น" onChange={(e) => setForceBuyerId(e.target.value)}>
                {buyers.filter((b) => b.dispatchLineId || b.lineGroupId).map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              disabled={!forceBuyerId || busy}
              onClick={() => window.confirm('ส่งการ์ดให้คนนี้ทันที ไม่สนเวลา/เรือแอร์/ร้าน — งานนี้จะเป็นของเขา ไม่ส่งต่อใคร') && run((j) => onForceSend(j, Number(forceBuyerId)))}
            >
              ส่งเลย
            </Button>
          </Box>
        )}
        <Box flex={1} />
        <Button onClick={onClose}>ปิด</Button>
        {kind === 'inform' && (
          <Button variant="contained" color="success" disabled={busy} onClick={() => run(onInformed)}>แจ้งลูกค้าแล้ว ✓</Button>
        )}
        {canRefund && (
          <Button variant="contained" color="error" disabled={busy} onClick={() => window.confirm('ยืนยันว่าซื้อไม่ทันจริง? จะคืนเงินเข้า wallet ลูกค้าเต็มจำนวนที่จ่ายมา แจ้งลูกค้าทาง LINE และปิดงานนี้') && run(onRefundPurchase)}>
            {kind === 'askBuyer' ? 'คนกดบอกว่าไม่ได้กด → คืนเงินลูกค้า' : 'ซื้อไม่ทัน → คืนเงินลูกค้า'}
          </Button>
        )}
        {/* Sold task on an order: nothing moved on its own — the admin confirms, then the order enters the cancel tab (owner 2026-11-01). */}
        {kind === 'fyi' && !canRefund && !job.adminAcknowledgedAt && (
          <Button variant="contained" color="error" disabled={busy} onClick={() => window.confirm('ยืนยันว่าซื้อไม่ทันจริง? รายการจะเข้าหน้า ดำเนินการยกเลิก (คืนเงินและแจ้งลูกค้าที่นั่น)') && run(onCancelOrder)}>
            ยืนยันซื้อไม่ทัน → เข้าหน้ายกเลิก
          </Button>
        )}
        {/* double proof: the owner handles it — admins just close; only a superuser clears the task */}
        {kind === 'double' && isSuperuser && <Button variant="contained" disabled={busy} onClick={() => run(onAcknowledge)}>รับทราบ (คุณเกน)</Button>}
      </DialogActions>

      <ImageUploadModal
        isOpen={pasteOpen}
        onRequestClose={() => setPasteOpen(false)}
        onConfirm={(files) => setProofFile(files[0] || null)}
        title="วางรูปยืนยันการสั่งซื้อ (Ctrl+V)"
        maxFiles={1}
        maxFileSize={15 * 1024 * 1024}
      />
    </Dialog>
  );
}
